<template>
  <div>
    <div v-if="!demand" class="loader"></div>
    <b-container fluid v-else>
      <b-row>
        <b-col class="d-flex">
          <backlink class="mt-20" />
          <div class="flex-fill">
            <h1>
              Location pour compte
              <div class="subtitle">{{ demand.number }}</div>
              <b-badge :variant="$store.getters['location_for_account/getStatusVariant'](statusSlug)">{{ statusLabel }}</b-badge>
            </h1>
          </div>
          <div class="text-right">
            <b-button v-for="action in availableActions" :key="action.key" :variant="action.variant"
              size="lg" class="ml-10" @click="openAction(action)">
              {{ action.label }}
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Sollicitations du demandeur : ce ne sont pas des changements d'état, elles ne
           modifient ni les dates ni le statut. Elles attendent une réponse, et c'est la
           première chose que le gestionnaire doit voir en ouvrant la demande. -->
      <b-alert v-for="request in pendingRequests" :key="request.id" show variant="warning">
        <strong>{{ requestLabel(request.event_type) }}</strong>
        — {{ payloadOf(request).wished_date | moment("DD-MM-YYYY") }}
        <template v-if="request.comment"> · {{ request.comment }}</template>
        <b-button size="sm" variant="outline-primary" class="ml-3" @click="openAnswer(request)">
          Répondre
        </b-button>
      </b-alert>

      <b-row>
        <b-col md="6">
          <div class="ibox">
            <div class="ibox-title"><i class="far fa-user"></i>Contexte</div>
            <div class="ibox-content">
              <dl class="detail-list">
                <dt>Demandeur :</dt>
                <dd>
                  <router-link v-if="demand.requester && $root.isAuthorized('user_show')"
                    :to="{ name: 'UserDetail', params: { id: demand.requester.id } }" target="_blank">{{ fullName(demand.requester) }}</router-link>
                  <template v-else>{{ fullName(demand.requester) || "-" }}</template>
                  <template v-if="demand.requester_email">
                    — <a :href="'mailto:' + demand.requester_email">{{ demand.requester_email }}</a>
                  </template>
                </dd>
                <dt>Agence :</dt>
                <dd>
                  <router-link v-if="demand.agency && $root.isAuthorized('agency_show')"
                    :to="{ name: 'AgencyDetail', params: { id: demand.agency.id } }" target="_blank">{{ demand.agency.internal_number }}</router-link>
                  <template v-else>{{ (demand.agency ? demand.agency.internal_number : null) || "-" }}</template>
                </dd>
                <dt>Affaire :</dt>
                <dd>
                  <router-link v-if="demand.business && $root.isAuthorized('business_show')"
                    :to="{ name: 'BusinessDetail', params: { id: demand.business.id } }" target="_blank">{{ demand.business.number }}</router-link>
                  <template v-else>{{ (demand.business ? demand.business.number : null) || "-" }}</template>
                </dd>
                <dt>Chantier :</dt>
                <dd>{{ demand.site_name || "-" }}</dd>
                <dt>Adresse :</dt>
                <dd>{{ demand.site_address }} — {{ demand.site_zip_code }} {{ demand.site_city }}</dd>
                <dt>Téléphone :</dt>
                <dd>
                  <a v-if="demand.phone_number" :href="'tel:' + demand.phone_number">
                    {{ demand.phone_number | phone }}
                  </a>
                  <template v-else>-</template>
                </dd>
                <dt>Gestionnaire :</dt>
                <dd>
                  <router-link v-if="demand.handled_by && $root.isAuthorized('user_show')"
                    :to="{ name: 'UserDetail', params: { id: demand.handled_by.id } }" target="_blank">{{ fullName(demand.handled_by) }}</router-link>
                  <template v-else>{{ demand.handled_by ? fullName(demand.handled_by) : "Non pris en charge" }}</template>
                </dd>
              </dl>
            </div>
          </div>

          <div class="ibox">
            <div class="ibox-title"><i class="far fa-toolbox"></i>Matériel demandé</div>
            <div class="ibox-content">
              <dl class="detail-list">
                <dt>Matériel :</dt>
                <dd>
                  <router-link v-if="demand.product && $root.isAuthorized('product_show')"
                    :to="{ name: 'ProductDetail', params: { id: demand.product.id } }" target="_blank">{{ demand.product.name }}</router-link>
                  <template v-else>{{ (demand.product ? demand.product.name : null) || "-" }}</template>
                </dd>
                <dt>Quantité :</dt>
                <dd>{{ demand.quantity }}</dd>
                <!-- Dates « souhaitées » : elles expriment le besoin du demandeur. Ce sont
                     celles du gestionnaire qui font foi pour le suivi et la facturation. -->
                <dt>Dates souhaitées :</dt>
                <dd>{{ dateRange(demand.wished_start_date, demand.wished_end_date) }}</dd>
                <template v-if="demand.comment">
                  <dt>Commentaire :</dt>
                  <dd>{{ demand.comment }}</dd>
                </template>
              </dl>

              <template v-if="attributeValues.length">
                <h3 class="mt-20">Caractéristiques demandées</h3>
                <dl class="detail-list">
                  <template v-for="value in attributeValues">
                    <dt :key="'d' + value.id">{{ value.request_attribute.label }} :</dt>
                    <dd :key="'v' + value.id">{{ attributeValueText(value) }}</dd>
                  </template>
                </dl>
              </template>
            </div>
          </div>

          <!-- Pièce jointe de la demande et bon de commande de l'acceptation. PDF et images
               s'ouvrent dans une fenêtre ; le reste (Word, Excel…) se télécharge. -->
          <div v-if="attachments.length" class="ibox">
            <div class="ibox-title"><i class="far fa-paperclip"></i>Pièces jointes</div>
            <div class="ibox-content">
              <ul class="lpc-attachments">
                <li v-for="(attachment, index) in attachments" :key="attachment.id">
                  <a v-if="attachment.preview" :href="fileUrl(attachment)" target="_blank" rel="noopener"
                    @click.prevent="openAttachment(attachment, index)">{{ attachment.label }}</a>
                  <a v-else :href="fileUrl(attachment)" :download="downloadName(attachment, index)">{{ attachment.label }}</a>
                  <span class="text-muted">
                    · {{ attachment.extension.toUpperCase() }}
                    <template v-if="attachment.created_at"> · {{ attachment.created_at | moment("DD-MM-YYYY") }}</template>
                    <template v-if="!attachment.preview"> · téléchargement</template>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </b-col>

        <b-col md="6">
          <div class="ibox">
            <div class="ibox-title"><i class="far fa-euro-sign"></i>Chiffrage</div>
            <div class="ibox-content">
              <p v-if="!demand.company && !announcedDailyPrice" class="text-muted mb-0">
                La demande n'a pas encore été chiffrée.
              </p>
              <dl v-else class="detail-list">
                <dt>Loueur :</dt>
                <dd>
                  <router-link v-if="demand.company && $root.isAuthorized('company_show')"
                    :to="{ name: 'CompanyDetail', params: { id: demand.company.id } }" target="_blank">{{ demand.company.name }}</router-link>
                  <template v-else>{{ (demand.company ? demand.company.name : null) || "-" }}</template>
                </dd>
                <template v-if="demand.rental_contract_reference">
                  <dt>Référence du contrat loueur :</dt>
                  <dd>{{ demand.rental_contract_reference }}</dd>
                </template>
                <!-- « En vigueur » ne vaut que pour une location commencée : avant, c'est le
                     prix proposé qu'il faut lire, et l'écran affichait « — » sur toute demande
                     chiffrée en attente de réponse. -->
                <dt>{{ currentDailyPrice ? "Prix journalier HT en vigueur :" : "Prix journalier HT proposé :" }}</dt>
                <dd>{{ announcedDailyPrice ? announcedDailyPrice.toFixed(2) + "€" : "—" }}</dd>
                <template v-if="estimatedTotal && estimatedTotal.total > 0">
                  <dt>Montant prévisionnel HT :</dt>
                  <dd>
                    <strong>{{ estimatedTotal.total.toFixed(2) }}€</strong>
                    <span class="text-muted">
                      — {{ estimatedTotal.days }} jour(s) facturable(s){{ demand.quantity > 1 ? " × " + demand.quantity + " ex." : "" }}
                    </span>
                  </dd>
                </template>
                <dt>Dates prévues :</dt>
                <dd>{{ dateRange(demand.planned_start_date, demand.planned_end_date) }}</dd>
                <template v-if="demand.effective_start_date">
                  <dt>Mise à disposition :</dt>
                  <dd>{{ formatDate(demand.effective_start_date) }}</dd>
                </template>
                <template v-if="demand.effective_end_date">
                  <dt>Restitution :</dt>
                  <dd>{{ formatDate(demand.effective_end_date) }}</dd>
                </template>
                <template v-if="demand.provided_equipment_label">
                  <dt>Matériel mis à disposition :</dt>
                  <dd>
                    {{ demand.provided_equipment_label }}

                  </dd>
                </template>
              </dl>

              <!-- Le prix est historisé : chaque renégociation ou prolongation ajoute une ligne
                   datée, et la facturation applique à chaque jour le prix en vigueur ce jour-là. -->
              <template v-if="prices.length > 1">
                <h3 class="mt-20">Historique du prix journalier</h3>
                <ul class="lpc-prices">
                  <li v-for="price in prices" :key="price.id">
                    À partir du {{ price.effective_from | moment("DD-MM-YYYY") }} :
                    <strong>{{ parseFloat(price.daily_price_ht).toFixed(2) }}€</strong>
                    <template v-if="price.comment"> — {{ price.comment }}</template>
                  </li>
                </ul>
              </template>
            </div>
          </div>

          <div class="ibox">
            <div class="ibox-title lpc-title--action">
              <i class="far fa-file-invoice-dollar"></i>Lignes de frais
              <b-button size="sm" variant="outline-primary" class="ml-auto" @click="openFee()">Ajouter</b-button>
            </div>
            <div class="ibox-content">
              <p v-if="!fees.length" class="text-muted mb-0">Aucune ligne de frais.</p>
              <template v-else>
                <div class="table-wrapper">
                  <b-table small :items="fees" :fields="feeFields">
                    <template slot="category" slot-scope="data">
                      {{ $store.getters["location_for_account/getFeeCategoryLabel"](data.value) }}
                    </template>
                    <template slot="total_ht" slot-scope="data">
                      {{ parseFloat(data.value).toFixed(2) }}€
                    </template>
                    <template slot="attached_at" slot-scope="data">{{ data.value | moment("DD-MM-YYYY") }}</template>
                    <template slot="billing_state" slot-scope="data">
                      <span :class="{ 'text-muted': data.value === 'billed' }">
                        {{ $store.getters["location_for_account/getFeeStateLabel"](data.value) }}
                        <template v-if="data.item.manual_billing">
                          (<router-link v-if="$root.isAuthorized('manual-billing_show')"
                            :to="{ name: 'ManualBillingDetail', params: { id: data.item.manual_billing.id } }"
                            target="_blank">{{ data.item.manual_billing.manual_billing_number }}</router-link><template
                            v-else>{{ data.item.manual_billing.manual_billing_number }}</template>)
                        </template>
                      </span>
                    </template>
                    <template slot="actions" slot-scope="data">
                      <!-- Une ligne facturée est verrouillée : elle est partie chez le client, la
                           corriger ici la ferait diverger de la pièce comptable. -->
                      <template v-if="data.item.billing_state !== 'billed'">
                        <a href="#" @click.prevent="openFee(data.item)"><i class="far fa-pen"></i></a>
                        <a href="#" class="ml-2" @click.prevent="removeFee(data.item)"><i class="far fa-trash"></i></a>
                      </template>
                      <i v-else class="far fa-lock text-muted" title="Ligne facturée"></i>
                    </template>
                  </b-table>
                </div>
                <p class="mt-2 mb-0 text-right">
                  En attente du prochain cycle : <strong>{{ pendingFeesTotal.toFixed(2) }}€ HT</strong>
                </p>
              </template>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <div class="ibox">
            <div class="ibox-title"><i class="far fa-history"></i>Historique</div>
            <div class="ibox-content">
              <div class="table-wrapper">
                <b-table hover small show-empty :items="logs" :fields="historyFields">
                  <template slot="empty">
                    <div class="text-center">Aucun évènement pour le moment</div>
                  </template>
                  <!-- L'API sérialise `createdAt` en `created_at` ; le filtre `moment`
                       appliqué à une valeur absente afficherait l'instant présent. -->
                  <template slot="created_at" slot-scope="data">
                    {{ data.value ? $moment(data.value).format("DD-MM-YYYY HH:mm") : "-" }}
                  </template>
                  <template slot="event" slot-scope="data">
                    <strong>{{ eventLabel(data.item) }}</strong>
                  </template>
                  <template slot="details" slot-scope="data">
                    <span class="text-muted">{{ payloadSummary(data.item) || "-" }}</span>
                  </template>
                  <template slot="comment" slot-scope="data">
                    <span class="nl2br">{{ data.value || "-" }}</span>
                  </template>
                  <template slot="user" slot-scope="data">{{ fullName(data.value) || "-" }}</template>
                </b-table>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-modal id="lpc-attachment" size="xl" centered :title="currentAttachment ? currentAttachment.label : ''"
        @hidden="currentAttachment = null">
        <template v-if="currentAttachment">
          <iframe v-if="currentAttachment.preview === 'pdf'" :src="fileUrl(currentAttachment)"
            :title="currentAttachment.label" class="lpc-attachment-viewer"></iframe>
          <img v-else :src="fileUrl(currentAttachment)" :alt="currentAttachment.label" class="lpc-attachment-image" />
        </template>
        <div slot="modal-footer">
          <a v-if="currentAttachment" :href="fileUrl(currentAttachment)"
            :download="downloadName(currentAttachment, currentAttachmentIndex)" class="btn btn-outline-primary">Télécharger</a>
          <b-button variant="primary" class="ml-10" @click="$root.$emit('bv::hide::modal', 'lpc-attachment')">Fermer</b-button>
        </div>
      </b-modal>

      <!-- Une seule modale sert toutes les actions : leurs champs diffèrent, leur forme non. -->
      <!-- `@ok.prevent` : sans lui, bootstrap-vue ferme la fenêtre au clic sur Valider, quoi
           que fasse le gestionnaire — un champ oublié faisait disparaître la saisie avec elle.
           C'est `submitAction` qui ferme, et seulement une fois le serveur d'accord. -->
      <b-modal id="lpc-action" :title="currentAction ? currentAction.label : ''" @ok.prevent="submitAction"
        :ok-disabled="actionSubmitting" ok-title="Valider" cancel-title="Annuler"
        @hidden="resetActionForm">
        <!-- Une transition qui n'a rien à saisir ouvrait une fenêtre vide, réduite à ses deux
             boutons : elle dit maintenant ce qu'elle va faire, et « Valider » la confirme. -->
        <p v-if="currentAction && currentAction.confirm">{{ currentAction.confirm }}</p>
        <b-form-group v-for="field in (currentAction ? currentAction.fields : [])" :key="field.name"
          :label="field.label + (field.required ? ' *' : '')">
          <template v-if="field.type === 'select'">
            <!-- Autocomplete et non liste déroulante : le référentiel des sociétés se compte
                 en milliers, on y arrive par les premières lettres du nom. -->
            <options-autocomplete v-model="actionForm[field.name]" :options="fieldOptions(field)"
              variant="form" :placeholder="field.placeholder" />

            <!-- Un loueur absent du référentiel renvoyait le gestionnaire dans l'administration
                 des sociétés, chiffrage perdu en route. Il se crée dans sa propre fenêtre, et
                 le champ se remplit ensuite tout seul. -->
            <a v-if="field.creatable && canCreateCompany" href="#" class="lpc-renter__toggle"
              @click.prevent="openRenterModal(field.name)">
              <i class="far fa-plus"></i> Ce loueur n'est pas dans la liste
            </a>
          </template>
          <b-form-file v-else-if="field.type === 'file'" v-model="actionForm[field.name]"
            browse-text="Parcourir" placeholder="Aucun fichier sélectionné" />
          <b-form-textarea v-else-if="field.type === 'textarea'" v-model="actionForm[field.name]" rows="3" />
          <b-form-input v-else :type="field.type" v-model="actionForm[field.name]" :step="field.step" />

          <!-- L'erreur se lit sous le champ qui la porte : un toast en haut de page oblige à
               retrouver soi-même lequel des sept champs est en cause. -->
          <div v-if="actionErrors[field.name]" class="text-danger lpc-field-error">{{ actionErrors[field.name] }}</div>
        </b-form-group>
      </b-modal>

      <!-- Création d'un loueur, empilée par-dessus la modale de chiffrage. Déclarée APRÈS elle
           à dessein : la règle de z-index du back-office met toutes les fenêtres au même plan
           pour passer devant le menu, c'est donc l'ordre du DOM qui décide laquelle est au
           premier plan. -->
      <b-modal id="lpc-renter" lazy centered hide-footer no-close-on-backdrop @hidden="resetRenterForm">
        <template slot="modal-title">Nouveau loueur</template>
        <!-- Les six champs que l'API exige d'une société : le type est imposé (« loueur »),
             les cinq autres se saisissent. -->
        <b-form-group v-for="sub in renterFields" :key="sub.name"
          :label="sub.label + (sub.required ? ' *' : '')">
          <options-autocomplete v-if="sub.type === 'select'" v-model="renterForm[sub.name]"
            :options="companyGroupOptions" variant="form" placeholder="Recherchez un groupe…" />
          <b-form-input v-else :type="sub.type" v-model="renterForm[sub.name]" />
        </b-form-group>
        <b-button class="mt-3" variant="primary" block :disabled="renterSaving" @click="submitRenter">
          {{ renterSaving ? "Création…" : "Créer le loueur" }}
        </b-button>
        <b-button class="mt-3" variant="outline-secondary" block :disabled="renterSaving"
          @click="closeRenterModal">Annuler</b-button>
      </b-modal>

      <b-modal id="lpc-fee" :title="feeForm.id ? 'Modifier la ligne de frais' : 'Ajouter une ligne de frais'"
        @ok="submitFee" ok-title="Valider" cancel-title="Annuler">
        <b-form-group label="Libellé *"
          description="Un libellé absent de la bibliothèque s'utilise tel quel : il y entrera à l'enregistrement.">
          <!-- Liste maison plutôt qu'un `<datalist>` : celui-ci ne s'ouvre pas d'un clic dans
               le champ, et ne sait afficher que le libellé nu — on ne voyait donc pas ce que
               l'entrée choisie allait préremplir. La saisie libre, elle, reste entière. -->
          <div class="lpc-suggest">
            <b-form-input v-model="feeForm.label" autocomplete="off"
              placeholder="Saisissez un libellé, ou cliquez pour parcourir la bibliothèque…"
              @focus="openFeeLabels" @click="openFeeLabels" @input="onFeeLabelInput"
              @blur="closeFeeLabels" @keydown.down.prevent="moveFeeLabel(1)"
              @keydown.up.prevent="moveFeeLabel(-1)" @keydown.enter="onFeeLabelEnter"
              @keydown.esc="onFeeLabelEsc" />

            <ul v-if="feeLabelsOpen && (feeLabelMatches.length || feeLabelQuery)"
              ref="feeLabelList" class="lpc-suggest__list">
              <!-- `mousedown.prevent` : sans lui, le clic ferait d'abord perdre le focus au
                   champ, la liste se fermerait, et le `click` tomberait dans le vide. -->
              <li v-for="(entry, index) in feeLabelMatches" :key="entry.id"
                :class="['lpc-suggest__item', { 'is-active': index === feeLabelIndex }]"
                @mousedown.prevent="selectFeeLabel(entry)" @mouseenter="feeLabelIndex = index">
                <span class="lpc-suggest__label">{{ entry.label }}</span>
                <span v-if="feeLabelHint(entry)" class="lpc-suggest__meta">{{ feeLabelHint(entry) }}</span>
              </li>
              <li v-if="!feeLabelMatches.length" class="lpc-suggest__empty">
                Aucun libellé connu ne correspond : celui-ci entrera dans la bibliothèque à
                l'enregistrement.
              </li>
              <li v-else-if="feeLabelHidden" class="lpc-suggest__more">
                <i class="far fa-ellipsis-h"></i> {{ feeLabelHidden }} autre(s) — précisez la saisie
              </li>
            </ul>
          </div>
        </b-form-group>
        <b-form-group label="Catégorie *">
          <b-form-select v-model="feeForm.category"
            :options="$store.getters['location_for_account/getFeeCategoriesForSelect']" />
        </b-form-group>
        <b-form-group label="Quantité *">
          <b-form-input type="number" step="0.01" v-model="feeForm.quantity" />
        </b-form-group>
        <b-form-group label="Montant unitaire HT *">
          <b-form-input type="number" step="0.01" v-model="feeForm.unit_price_ht" />
        </b-form-group>
        <!-- Le mois révolu est fermé côté serveur : une ligne qui s'y rattacherait ne serait
             reprise par aucun cycle. Le champ le dit avant la saisie plutôt qu'après. -->
        <b-form-group label="Date de rattachement *"
          description="C'est elle qui détermine le cycle de facturation qui portera la ligne. Le mois en cours est le plus ancien possible : les cycles précédents sont passés.">
          <b-form-input type="date" v-model="feeForm.attached_at" :min="firstDayOfMonth" />
        </b-form-group>
        <b-form-group label="Commentaire">
          <b-form-textarea v-model="feeForm.comment" rows="2" />
        </b-form-group>
      </b-modal>

      <!-- La suppression est définitive et partait d'un simple clic sur la corbeille, voisine
           du crayon : elle se confirme désormais, la ligne visée rappelée en toutes lettres. -->
      <b-modal id="lpc-fee-delete" title="Supprimer la ligne de frais" centered
        ok-variant="danger" ok-title="Supprimer" cancel-title="Annuler" @ok="confirmRemoveFee">
        <p v-if="feeToDelete" class="mb-0">
          Supprimer la ligne <strong>{{ feeToDelete.label }}</strong>
          ({{ parseFloat(feeToDelete.total_ht).toFixed(2) }}€ HT) ?
        </p>
        <p class="text-muted mb-0 mt-2">Cette suppression est définitive.</p>
      </b-modal>
    </b-container>
  </div>
</template>

<script>
const EVENT_LABELS = {
  status_change: "Changement de statut",
  quotation: "Chiffrage",
  quotation_update: "Chiffrage corrigé",
  price_change: "Prix journalier",
  extension: "Prolongation",
  reduction: "Réduction de durée",
  extension_request: "Demande de prolongation",
  stop_request: "Demande d'arrêt",
  request_answer: "Réponse au demandeur",
  fee_added: "Ligne de frais ajoutée",
  fee_removed: "Ligne de frais supprimée",
  fee_billed: "Ligne de frais facturée",
  attachment_added: "Pièce jointe",
};

// Code du type de société « loueur », posé par la migration du chantier LPC. C'est lui qui
// range la société créée ici au bon endroit du référentiel.
const RENTER_TYPE_CODE = "LOUR";

// Ce que l'API réclame d'une société, hors type : elle refuse un POST incomplet, et ces
// colonnes sont NOT NULL en base. Rien à inventer côté client — le code SAP en particulier
// est une donnée de gestion.
const RENTER_FIELDS = [
  { name: "name", label: "Nom du loueur", type: "text", required: true },
  { name: "internal_code", label: "Code SAP", type: "text", required: true },
  { name: "company_group_id", label: "Groupe", type: "select", required: true },
  { name: "address", label: "Adresse", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
];

// Suggestions de libellés de frais montrées d'un coup. Au-delà, la liste invite à préciser
// la saisie plutôt qu'à dérouler toute la bibliothèque.
const FEE_LABEL_LIMIT = 20;

// Minuscules, accents retirés : « peage » doit trouver « Péage ». Les autocompletes du
// back-office portent chacun la leur, il n'y a pas d'utilitaire partagé.
function normalizeLabel(value) {
  return (value == null ? "" : String(value))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // marques diacritiques combinantes
}

// Paramètres d'URL qui portent la modale ouverte. Ils s'excluent : une seule modale à la
// fois, et changer de sujet efface le précédent.
const MODAL_PARAMS = ["action", "answer", "fee"];

export default {
  name: "LocationForAccountDetail",
  data() {
    return {
      currentAction: null,
      // Pièce jointe affichée dans la fenêtre d'aperçu.
      currentAttachment: null,
      currentAttachmentIndex: 0,
      actionForm: {},
      // Erreurs de saisie, indexées par nom de champ : elles s'affichent sous le champ
      // concerné et disparaissent dès qu'il est renseigné.
      actionErrors: {},
      actionSubmitting: false,
      feeForm: {},
      // Liste de suggestions du champ Libellé : ouverte au clic, filtrée à la frappe.
      // `feeLabelQuery` est distincte de `feeForm.label` — rouvrir la liste sur un libellé
      // déjà saisi doit remontrer toute la bibliothèque, pas la seule entrée qui s'y trouve.
      feeLabelsOpen: false,
      feeLabelQuery: "",
      feeLabelIndex: -1,
      // Ligne de frais dont la suppression attend confirmation.
      feeToDelete: null,
      renterFields: RENTER_FIELDS,
      // Nom du champ que la création alimentera, ou null quand le bloc est replié.
      renterField: null,
      renterForm: {},
      renterSaving: false,
      historyFields: [
        { key: "created_at", label: "Date", tdClass: "text-nowrap" },
        { key: "event", label: "Événement" },
        { key: "details", label: "Détail" },
        { key: "comment", label: "Commentaire" },
        { key: "user", label: "Auteur" },
      ],
      feeFields: [
        { key: "label", label: "Libellé" },
        { key: "category", label: "Catégorie" },
        { key: "quantity", label: "Qté" },
        { key: "total_ht", label: "Total HT" },
        { key: "attached_at", label: "Rattachement" },
        { key: "billing_state", label: "État" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    detail() {
      return this.$store.getters["location_for_account/getDetail"];
    },
    demand() {
      return this.detail ? this.detail.locationForAccount : null;
    },
    statusSlug() {
      return this.demand && this.demand.status ? this.demand.status.status_slug : null;
    },
    statusLabel() {
      return this.$store.getters["location_for_account/getStatusLabel"](this.statusSlug);
    },
    /**
     * Prix à AFFICHER : celui en vigueur si la location a commencé, sinon celui du chiffrage.
     * Calculé côté serveur, où vit déjà la règle (`getQuotedDailyPrice`).
     */
    announcedDailyPrice() {
      return this.detail ? this.detail.quotedDailyPrice : null;
    },
    /**
     * Premier jour du mois courant : borne basse de la date de rattachement d'un frais.
     * Au-delà, le cycle a déjà tourné et la ligne resterait « à facturer » sans jamais partir.
     */
    firstDayOfMonth() {
      const now = new Date();

      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    },
    /** Montant prévisionnel sur toute la durée, calculé par le moteur de facturation. */
    estimatedTotal() {
      return this.detail ? this.detail.estimatedTotal : null;
    },
    currentDailyPrice() {
      return this.detail ? this.detail.currentDailyPrice : null;
    },
    pendingFeesTotal() {
      return this.detail ? this.detail.pendingFeesTotal || 0 : 0;
    },
    pendingRequests() {
      return this.detail ? this.detail.pendingRequests || [] : [];
    },
    prices() {
      return this.demand && this.demand.prices ? this.demand.prices : [];
    },
    fees() {
      return this.demand && this.demand.fees ? this.demand.fees : [];
    },
    logs() {
      return this.demand && this.demand.logs ? [...this.demand.logs].reverse() : [];
    },
    attachments() {
      return this.detail && this.detail.attachments ? this.detail.attachments : [];
    },
    // L'API sérialise en snake_case (JMS) : `attribute_values`, pas `attributeValues`.
    attributeValues() {
      return this.demand && this.demand.attribute_values ? this.demand.attribute_values : [];
    },
    feeLabels() {
      return this.$store.getters["fee_label/getSuggestions"];
    },
    /**
     * Bibliothèque filtrée par la saisie, les correspondances les plus à gauche en tête
     * (« port » propose « Port » avant « Frais de port »). Sans saisie : tout, dans l'ordre
     * alphabétique où l'API la sert.
     */
    filteredFeeLabels() {
      const query = normalizeLabel(this.feeLabelQuery).trim();
      if (!query) {
        return this.feeLabels;
      }

      return this.feeLabels
        .map((entry) => ({ entry, at: normalizeLabel(entry.label).indexOf(query) }))
        .filter((row) => row.at >= 0)
        .sort((a, b) => a.at - b.at)
        .map((row) => row.entry);
    },
    feeLabelMatches() {
      return this.filteredFeeLabels.slice(0, FEE_LABEL_LIMIT);
    },
    feeLabelHidden() {
      return Math.max(0, this.filteredFeeLabels.length - FEE_LABEL_LIMIT);
    },
    /**
     * Actions offertes au gestionnaire, dérivées du statut. La table des transitions vit
     * côté serveur et refusera de toute façon ce qui n'est pas permis : cette liste n'est
     * qu'une commodité d'écran, pas la règle.
     */
    availableActions() {
      const actions = [];
      const status = this.statusSlug;

      if (status === "location-for-account-soumise") {
        actions.push({
          key: "take-in-charge", label: "Prendre en charge", variant: "primary", fields: [],
          confirm: "La demande passe en traitement et vous en devenez le gestionnaire : "
            + "votre nom devient visible du demandeur, et vous pourrez la chiffrer.",
        });
      }
      if (status === "location-for-account-en-traitement") {
        actions.push({
          key: "quote", label: "Chiffrer", variant: "primary",
          fields: this.quoteFields,
          defaults: {
            planned_start_date: this.isoDate(this.demand.wished_start_date),
            planned_end_date: this.isoDate(this.demand.wished_end_date),
          },
        });
      }
      if (status === "location-for-account-acceptee") {
        actions.push({
          key: "start", label: "Mise à disposition", variant: "primary",
          fields: [{ name: "effective_start_date", label: "Date de début effective", type: "date", required: true }],
          defaults: { effective_start_date: this.today() },
        });
      }
      if (status === "location-for-account-en-location") {
        actions.push({
          key: "change-end-date", label: "Prolonger / Réduire", variant: "outline-primary",
          fields: this.durationFields({ reasonName: "reason", reasonLabel: "Motif", required: true }),
          defaults: { planned_end_date: this.isoDate(this.demand.planned_end_date) },
        });
        actions.push({
          key: "close", label: "Clore", variant: "primary",
          fields: [{ name: "effective_end_date", label: "Date de fin effective (restitution)", type: "date", required: true }],
          defaults: { effective_end_date: this.today() },
        });
      }
      // Le gestionnaire peut se prononcer POUR LE COMPTE du demandeur (SFD 2.11) : un appel
      // suffit souvent à emporter la décision, et l'obliger à se connecter pour cliquer
      // ferait perdre des jours. L'historique conserve l'auteur réel.
      if (status === "location-for-account-chiffree") {
        // Corriger plutôt que refaire : une erreur de prix ou de date se rattrapait en
        // attendant le refus du demandeur, puis en reprenant la demande à zéro. Le serveur
        // refuse l'appel dès que le demandeur s'est prononcé.
        actions.push({
          key: "update-quote", label: "Modifier le chiffrage", variant: "outline-primary",
          fields: this.quoteFields,
          defaults: {
            company_id: this.demand.company ? this.demand.company.id : null,
            rental_contract_reference: this.demand.rental_contract_reference,
            daily_price_ht: this.quotedDailyPrice,
            planned_start_date: this.isoDate(this.demand.planned_start_date),
            planned_end_date: this.isoDate(this.demand.planned_end_date),
            provided_equipment_label: this.demand.provided_equipment_label,
            answer_comment: this.demand.answer_comment,
          },
        });
        actions.push({
          key: "accept", label: "Accepter pour le demandeur", variant: "primary",
          fields: [
            // Le bon de commande reste facultatif : son absence ne bloque pas.
            { name: "purchase_order", label: "Bon de commande (facultatif)", type: "file" },
            { name: "comment", label: "Commentaire", type: "textarea" },
          ],
        });
        actions.push({
          key: "refuse", label: "Refuser pour le demandeur", variant: "outline-danger",
          fields: [{ name: "reason", label: "Motif du refus", type: "textarea" }],
        });
      }

      // L'annulation reste ouverte tant que la facturation n'a pas démarré.
      if (
        ["location-for-account-soumise", "location-for-account-en-traitement",
         "location-for-account-chiffree", "location-for-account-acceptee"].indexOf(status) >= 0
      ) {
        actions.push({
          key: "cancel", label: "Annuler", variant: "outline-danger",
          fields: [{ name: "reason", label: "Motif", type: "textarea" }],
        });
      }

      return actions;
    },
    /**
     * Champs du chiffrage. Partagés par le chiffrage initial et sa correction : la correction
     * reprend la même proposition, ce n'est pas un autre formulaire — deux listes divergeraient
     * au premier champ ajouté d'un seul côté.
     */
    quoteFields() {
      return [
        {
          // Le loueur est celui auprès de qui ESM engage la location : sans lui, la demande
          // serait chiffrée sans qu'on sache qui fournit le matériel, et la facture d'achat
          // ne se rapprocherait de rien.
          name: "company_id", label: "Loueur", type: "select", required: true,
          // `optionsFrom` et non `options` : la liste est relue à chaque rendu, sans quoi
          // un loueur créé dans la modale n'apparaîtrait pas dans le champ qui l'a créé.
          optionsFrom: "companyOptions", placeholder: "Recherchez un loueur…",
          creatable: true,
        },
        { name: "rental_contract_reference", label: "Référence du contrat loueur", type: "text" },
        { name: "daily_price_ht", label: "Prix journalier HT", type: "number", step: "0.01", required: true },
        { name: "planned_start_date", label: "Date de début prévue", type: "date", required: true },
        { name: "planned_end_date", label: "Date de fin prévue", type: "date", required: true },
        { name: "provided_equipment_label", label: "Matériel mis à disposition (désignation, modèle, codes des exemplaires)", type: "text" },
        { name: "answer_comment", label: "Commentaire de réponse", type: "textarea" },
      ];
    },
    /**
     * Prix posé par le chiffrage : celui dont la date d'effet est la date de début prévue.
     *
     * `currentDailyPrice` ne convient pas ici — il donne le prix EN VIGUEUR AUJOURD'HUI, donc
     * rien tant que la location n'a pas commencé. Le formulaire de correction s'ouvrirait sans
     * son prix sur toute demande dont le début est à venir, c'est-à-dire presque toutes.
     */
    quotedDailyPrice() {
      const from = this.isoDate(this.demand.planned_start_date);
      const price = this.prices.find((item) => this.isoDate(item.effective_from) === from);

      return price ? price.daily_price_ht : this.announcedDailyPrice;
    },
    companyOptions() {
      return (
        this.$store.getters["company/getByTypeCode"](RENTER_TYPE_CODE).selectOptions("id", "name")
      );
    },
    companyGroupOptions() {
      return this.$store.getters["company_group/getAll"].selectOptions("id", "name");
    },
    /** L'API refuserait la création : ne pas proposer un geste qui finirait en erreur. */
    canCreateCompany() {
      return this.$store.getters["auth/isAuthorized"]("company_new");
    },
  },
  created() {
    this.$store.dispatch("location_for_account/getOptions");
    this.$store.dispatch("fee_label/getAll");

    // Réouverture de la modale inscrite dans l'URL. Elle attend les deux chargements : les
    // actions offertes se déduisent du statut de la demande, et le chiffrage propose la
    // liste des loueurs — ouverte trop tôt, elle s'afficherait sans son sélecteur.
    Promise.all([
      this.$store.dispatch("location_for_account/getDetail", this.$route.params.id),
      this.$store.dispatch("company/getAllForSelect"),
    ])
      .then(() => this.restoreModal())
      .catch(() => {});

    // Refermer une modale retire son paramètre : l'URL ne garde pas un sujet abandonné.
    this.$root.$on("bv::modal::hidden", this.onModalHidden);
  },
  beforeDestroy() {
    this.$root.$off("bv::modal::hidden", this.onModalHidden);
  },
  watch: {
    // Corriger un champ doit effacer son erreur tout de suite : la laisser jusqu'au prochain
    // clic sur Valider donnerait à croire que la correction n'a pas été prise.
    actionForm: {
      deep: true,
      handler(form) {
        Object.keys(this.actionErrors).forEach((name) => {
          if (form[name]) {
            this.$delete(this.actionErrors, name);
          }
        });
      },
    },
  },
  methods: {
    fileUrl(attachment) {
      return process.env.BACK_FILES_URL + attachment.file;
    },
    // Le fichier est stocké sous un nom technique : on le renomme d'après la demande.
    downloadName(attachment, index) {
      return this.demand.number + "-" + (index + 1) + "." + attachment.extension;
    },
    openAttachment(attachment, index) {
      this.currentAttachment = attachment;
      this.currentAttachmentIndex = index;
      this.$root.$emit("bv::show::modal", "lpc-attachment");
    },
    fullName(user) {
      return user ? [user.firstname, user.lastname].filter(Boolean).join(" ") : "";
    },
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    isoDate(value) {
      return value ? String(value).slice(0, 10) : null;
    },
    /**
     * Le filtre `moment` appliqué à une date absente n'affiche rien — la fiche montrait
     * alors une flèche entre deux vides, qu'on lit comme un défaut d'affichage plutôt que
     * comme une donnée manquante. Une demande reprise avant que les dates ne soient rendues
     * obligatoires côté serveur peut légitimement n'en porter aucune.
     */
    formatDate(value) {
      return value ? this.$moment(value).format("DD-MM-YYYY") : "-";
    },
    /**
     * Caractéristique telle que saisie par le demandeur. Un choix multiple est stocké une
     * valeur par ligne, une date en AAAA-MM-JJ ; l'unité suit la valeur, comme sur la fiche
     * de la boutique.
     */
    attributeValueText(value) {
      const attribute = value.request_attribute || {};
      let text = value.value || "";

      if (attribute.field_type === "multiple_choice") {
        text = text.split("\n").join(", ");
      } else if (attribute.field_type === "date") {
        text = this.formatDate(text);
      }

      return attribute.unit ? text + " " + attribute.unit : text;
    },
    dateRange(start, end) {
      if (!start && !end) {
        return "Non précisées";
      }

      return this.formatDate(start) + " → " + this.formatDate(end);
    },
    payloadOf(log) {
      try {
        return log.payload ? JSON.parse(log.payload) : {};
      } catch (e) {
        return {};
      }
    },
    eventLabel(log) {
      const label = EVENT_LABELS[log.event_type] || log.event_type;
      if (log.event_type === "status_change" && log.to_status) {
        return label + " → " + log.to_status.name;
      }
      return label;
    },
    requestLabel(eventType) {
      return EVENT_LABELS[eventType] || eventType;
    },
    /** Une sollicitation du demandeur, par opposition à un événement du gestionnaire. */
    isRequest(log) {
      return ["extension_request", "stop_request"].indexOf(log.event_type) >= 0;
    },
    /**
     * Résumé lisible du payload d'un événement. Le journal stocke des données brutes — dates
     * ISO, montants nus — que le commentaire ne reprend pas : sans mise en forme, ce que
     * l'événement a réellement changé reste invisible dans l'historique.
     */
    payloadSummary(log) {
      const payload = this.payloadOf(log);
      const date = (value) => (value ? this.$moment(value).format("DD-MM-YYYY") : "—");
      const price = (value) => parseFloat(value).toFixed(2) + "€";
      const parts = [];

      if (undefined !== payload.new_end_date) {
        parts.push("Fin prévue : " + date(payload.previous_end_date) + " → " + date(payload.new_end_date));
      }
      if (undefined !== payload.wished_date) {
        parts.push("Date souhaitée : " + date(payload.wished_date));
      }
      const dailyPrice = payload.new_daily_price_ht !== undefined ? payload.new_daily_price_ht : payload.daily_price_ht;
      if (undefined !== dailyPrice) {
        parts.push(
          price(dailyPrice) + " HT / jour" +
          (payload.effective_from !== undefined ? " à partir du " + date(payload.effective_from) : "")
        );
      }
      if (undefined !== payload.total_ht) {
        parts.push(price(payload.total_ht) + " HT");
      }
      if (undefined !== payload.attached_at) {
        parts.push("rattachée au " + date(payload.attached_at));
      }
      // Le SFD 2.11 a supprimé ce caractère ; le journal des lignes antérieures le porte
      // encore, et l'historique doit rester lisible tel qu'il a été écrit.
      if (false === payload.rebillable) {
        parts.push("non refacturable (règle antérieure)");
      }
      if (undefined !== payload.total && undefined !== payload.days) {
        parts.push(price(payload.total) + " HT sur " + payload.days + " jour(s)");
      }
      // Une sollicitation ne porte pas d'événement de réponse : la suite qui lui a été
      // donnée se lit sur le log lui-même.
      if (log.answered_at) {
        parts.push(
          "répondu le " + this.$moment(log.answered_at).format("DD-MM-YYYY") +
          (log.answered_by ? " par " + this.fullName(log.answered_by) : "")
        );
      } else if (this.isRequest(log)) {
        parts.push("en attente de réponse");
      }

      return parts.join(" · ");
    },
    /**
     * Inscrit la modale ouverte dans l'URL — `?action=<transition>`, `?answer=<sollicitation>`,
     * `?fee=<ligne|new>`. Un rafraîchissement la rouvre sur le même sujet, et l'adresse se
     * transmet telle quelle à un collègue. `replace` et non `push` : le bouton Précédent doit
     * quitter la fiche, pas défiler l'historique des ouvertures.
     *
     * `param` à null referme : le paramètre disparaît sans qu'un autre le remplace.
     */
    setModalQuery(param, value) {
      const query = { ...this.$route.query };
      MODAL_PARAMS.forEach((name) => delete query[name]);
      if (param) {
        query[param] = String(value);
      }
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
    onModalHidden(bvEvent, modalId) {
      if (modalId === "lpc-action" || modalId === "lpc-fee") {
        this.setModalQuery(null, null);
      }
      // La confirmation ne s'inscrit pas dans l'URL — une suppression ne se partage pas par
      // lien —, mais elle oublie sa cible en se refermant, annulée comme validée.
      if (modalId === "lpc-fee-delete") {
        this.feeToDelete = null;
      }
    },
    /**
     * Rouvre la modale que porte l'URL, une fois la demande chargée.
     *
     * Le sujet peut avoir disparu entre le partage du lien et son ouverture : la transition
     * n'est plus offerte parce que le statut a changé, la sollicitation a reçu sa réponse, la
     * ligne de frais a été supprimée. La fiche s'affiche alors normalement, paramètre retiré —
     * ouvrir une modale sans objet vaudrait moins qu'un écran ordinaire.
     */
    restoreModal() {
      const query = this.$route.query;

      if (query.action) {
        const action = this.availableActions.find((item) => item.key === query.action);
        if (action) {
          this.openAction(action);
          return;
        }
      } else if (query.answer) {
        const request = this.pendingRequests.find(
          (item) => String(item.id) === String(query.answer)
        );
        if (request) {
          this.openAnswer(request);
          return;
        }
      } else if (query.fee) {
        if (query.fee === "new") {
          this.openFee();
          return;
        }
        const fee = this.fees.find((item) => String(item.id) === String(query.fee));
        if (fee) {
          this.openFee(fee);
          return;
        }
      } else {
        return;
      }

      this.setModalQuery(null, null);
    },
    /**
     * Champs d'un changement de durée. L'action « Prolonger / Réduire » et la réponse à une
     * sollicitation les partagent : c'est la même opération, décidée seule ou à la demande du
     * locataire — deux listes divergeraient au premier champ ajouté d'un seul côté.
     *
     * Les deux paramètres portent ce qui les sépare. Le nom du champ de texte d'abord : la
     * transition journalise un motif, la réponse adresse un texte au demandeur, et l'API garde
     * son vocabulaire de chaque côté. L'obligation ensuite : répondre sans nouvelle date, c'est
     * refuser, et le refus doit rester possible.
     */
    durationFields({ reasonName, reasonLabel, required }) {
      return [
        { name: "planned_end_date", label: "Nouvelle date de fin prévue", type: "date", required },
        { name: reasonName, label: reasonLabel, type: "textarea", required },
        { name: "daily_price_ht", label: "Nouveau prix journalier HT (optionnel)", type: "number", step: "0.01" },
        { name: "price_effective_from", label: "Date d'effet du nouveau prix", type: "date" },
      ];
    },
    openAction(action) {
      this.startAction(action);
      this.setModalQuery("action", action.key);
    },
    /**
     * Réponse à une sollicitation : le formulaire du changement de durée, ouvert sur la date
     * que le demandeur souhaite.
     *
     * Répondre et appliquer étaient deux fenêtres et deux saisies — « Prolonger / Réduire »
     * pour poser la date, puis « Répondre » pour clore la sollicitation. Le gestionnaire
     * ressaisissait une date qu'il avait sous les yeux, et oubliait régulièrement la seconde
     * moitié : la location partait prolongée, l'alerte restait, le demandeur n'apprenait rien.
     * C'est une seule décision, elle tient désormais dans une seule fenêtre.
     */
    openAnswer(request) {
      const wished = this.isoDate(this.payloadOf(request).wished_date);

      this.startAction({
        key: "answer-request/" + request.id,
        label: this.requestLabel(request.event_type),
        // Ce que « Valider » va faire, dit avant la saisie : la fenêtre porte une date déjà
        // remplie, et rien n'indiquerait autrement qu'elle sera appliquée — ni comment
        // refuser.
        confirm: wished
          ? "Le demandeur souhaite le " + this.formatDate(wished) + ". Valider applique cette "
            + "date à la location et lui envoie votre réponse ; videz la date pour refuser, "
            + "seule la réponse partira."
          : "Valider envoie votre réponse au demandeur et referme la sollicitation.",
        fields: this.durationFields({
          reasonName: "comment",
          reasonLabel: "Réponse au demandeur",
          required: false,
        }),
        defaults: { planned_end_date: wished },
      });
      this.setModalQuery("answer", request.id);
    },
    /**
     * Ouvre la fenêtre sur une action, formulaire initialisé.
     *
     * Toutes les clés sont posées dès l'ouverture : Vue 2 ne rend pas réactive une propriété
     * ajoutée après coup, et un champ piloté par un composant — l'autocomplete — n'afficherait
     * jamais la valeur qu'on vient de lui donner.
     */
    startAction(action) {
      this.currentAction = action;

      const form = { ...(action.defaults || {}) };
      (action.fields || []).forEach((field) => {
        if (!(field.name in form)) {
          form[field.name] = null;
        }
      });
      this.actionForm = form;
      this.resetRenterForm();
      this.$root.$emit("bv::show::modal", "lpc-action");
    },
    /**
     * Valide la saisie, puis soumet. La fenêtre ne se ferme qu'une fois le serveur d'accord :
     * un refus de sa part — le loueur manquant, une transition interdite — laisse la saisie
     * en place plutôt que de la faire recommencer.
     */
    submitAction() {
      const action = this.currentAction;

      // Tous les champs manquants, pas seulement le premier : les signaler un par un ferait
      // recommencer autant de fois qu'il en manque.
      const errors = {};
      (action.fields || []).forEach((field) => {
        if (field.required && !this.actionForm[field.name]) {
          errors[field.name] = "Ce champ est obligatoire.";
        }
      });

      this.actionErrors = errors;
      if (Object.keys(errors).length) {
        return;
      }

      this.actionSubmitting = true;
      this.$store
        .dispatch("location_for_account/action", {
          id: this.$route.params.id,
          action: action.key,
          payload: this.actionForm,
        })
        .then(() => {
          this.$root.$emit("bv::hide::modal", "lpc-action");
        })
        // Le store a déjà signalé l'erreur du serveur : ici on se contente de garder la
        // fenêtre ouverte, ce que fait l'absence de `hide`.
        .catch(() => {})
        .then(() => {
          this.actionSubmitting = false;
        });
    },
    /** Remet la fenêtre à zéro, quelle que soit la façon dont elle s'est fermée. */
    resetActionForm() {
      this.actionErrors = {};
      this.actionSubmitting = false;
    },
    /**
     * Options d'un champ de liste. Relues à chaque rendu quand le champ nomme un `optionsFrom` :
     * `currentAction` est une copie inerte de l'action, et le référentiel, lui, bouge — un
     * loueur créé sans quitter la modale doit apparaître dans le champ qui l'a créé.
     */
    fieldOptions(field) {
      return field.optionsFrom ? this[field.optionsFrom] : field.options || [];
    },
    /**
     * Ouvre la fenêtre de création. Les référentiels ne sont chargés qu'ici : la très grande
     * majorité des chiffrages se passe d'eux.
     */
    openRenterModal(fieldName) {
      this.$store.dispatch("company_type/getAll");
      this.$store.dispatch("company_group/getAll");

      // Clés posées d'avance : Vue 2 ne rend pas réactive une propriété ajoutée après coup,
      // et l'autocomplete du groupe n'afficherait pas la valeur choisie.
      const form = {};
      RENTER_FIELDS.forEach((field) => {
        form[field.name] = null;
      });
      this.renterForm = form;
      this.renterField = fieldName;
      this.$root.$emit("bv::show::modal", "lpc-renter");
    },
    closeRenterModal() {
      this.$root.$emit("bv::hide::modal", "lpc-renter");
    },
    /** Branché sur `hidden` : la fenêtre se referme aussi par la croix ou par Échap. */
    resetRenterForm() {
      this.renterField = null;
      this.renterForm = {};
      this.renterSaving = false;
    },
    /**
     * Crée le loueur, puis le sélectionne dans le champ.
     *
     * La fiche complète est attendue avant de refermer : la liste du champ se filtre sur le
     * type porté par l'objet imbriqué, et l'entrée que le store pose à la volée ne l'a pas
     * encore — le loueur créé aurait disparu de sa propre liste le temps d'un aller-retour.
     */
    submitRenter() {
      const missing = RENTER_FIELDS.find(
        (field) => field.required && !this.renterForm[field.name]
      );
      if (missing) {
        this.$toastr.error("Veuillez renseigner le champ " + missing.label);
        return;
      }

      const field = this.renterField;
      this.renterSaving = true;

      // Le référentiel des types est réclamé à l'ouverture du bloc ; on repasse par le
      // dispatch, qui rend la main aussitôt s'il est déjà chargé et attend sinon — le clic
      // peut arriver avant la réponse.
      this.$store
        .dispatch("company_type/getAll")
        .then(() => {
          const type = this.$store.getters["company_type/getAll"].find(
            (item) => item.code === RENTER_TYPE_CODE
          );
          if (!type) {
            this.$toastr.error("Le type de société « loueur » est introuvable dans le référentiel.");
            return Promise.reject(new Error("missing-renter-type"));
          }

          return this.$store.dispatch("company/addItem", {
            ...this.renterForm,
            company_type_id: type.id,
          });
        })
        .then((response) => {
          const id = response.data.object.id;
          return this.$store
            .dispatch("company/getItem", { id: id, wait: true })
            .then(() => {
              this.$set(this.actionForm, field, id);
              this.closeRenterModal();
            });
        })
        .catch(() => {
          // Le message d'erreur vient du store, qui remonte déjà celui de l'API.
          this.renterSaving = false;
        });
    },
    openFee(fee) {
      this.feeForm = fee
        ? { ...fee, attached_at: this.isoDate(fee.attached_at) }
        : { quantity: 1, attached_at: this.today() };
      this.closeFeeLabels();
      this.setModalQuery("fee", fee ? fee.id : "new");
      this.$root.$emit("bv::show::modal", "lpc-fee");
    },
    /** Ouvre la liste sur la bibliothèque entière : le filtre ne se met en place qu'à la frappe. */
    openFeeLabels() {
      this.feeLabelQuery = "";
      this.feeLabelIndex = -1;
      this.feeLabelsOpen = true;
    },
    closeFeeLabels() {
      this.feeLabelsOpen = false;
      this.feeLabelIndex = -1;
    },
    onFeeLabelInput(value) {
      this.feeLabelQuery = value;
      this.feeLabelIndex = -1;
      this.feeLabelsOpen = true;
    },
    /** Flèches haut/bas : ouvrent la liste si elle est fermée, puis parcourent en boucle. */
    moveFeeLabel(step) {
      if (!this.feeLabelsOpen) {
        this.openFeeLabels();
        return;
      }

      const count = this.feeLabelMatches.length;
      if (!count) {
        return;
      }

      this.feeLabelIndex = (this.feeLabelIndex + step + count) % count;
      this.$nextTick(() => {
        const active = this.$refs.feeLabelList && this.$refs.feeLabelList.querySelector(".is-active");
        if (active) {
          active.scrollIntoView({ block: "nearest" });
        }
      });
    },
    /**
     * Échap referme la liste, et elle seule : sans arrêter la propagation, bootstrap-vue
     * fermerait la fenêtre entière dans la foulée et la saisie serait perdue.
     */
    onFeeLabelEsc(event) {
      if (!this.feeLabelsOpen) {
        return;
      }

      event.stopPropagation();
      this.closeFeeLabels();
    },
    /** Entrée ne valide une suggestion que si l'une est surlignée : sinon, la saisie libre passe. */
    onFeeLabelEnter(event) {
      if (!this.feeLabelsOpen || this.feeLabelIndex < 0) {
        return;
      }

      event.preventDefault();
      this.selectFeeLabel(this.feeLabelMatches[this.feeLabelIndex]);
    },
    /** Montant par défaut de l'entrée, ou null : l'API le sert absent, nul ou en chaîne. */
    feeLabelPrice(entry) {
      const price = entry.default_unit_price;

      return price === null || price === undefined || price === "" ? null : parseFloat(price);
    },
    /** Ce que l'entrée préremplira, résumé en bout de ligne. */
    feeLabelHint(entry) {
      const parts = [];
      const price = this.feeLabelPrice(entry);

      if (entry.category) {
        parts.push(this.$store.getters["location_for_account/getFeeCategoryLabel"](entry.category));
      }
      if (price !== null) {
        parts.push(price.toFixed(2) + "€ HT");
      }

      return parts.join(" · ");
    },
    /**
     * Applique l'entrée choisie : elle préremplit la catégorie et le montant qu'elle porte,
     * c'est tout l'intérêt d'y puiser plutôt que de retaper le libellé. Les valeurs restent
     * modifiables ensuite, et les changer ici n'altère pas la bibliothèque.
     *
     * `$set` : `feeForm` naît avec deux clés seulement, les autres ne seraient pas réactives.
     */
    selectFeeLabel(entry) {
      const price = this.feeLabelPrice(entry);

      this.$set(this.feeForm, "label", entry.label);

      if (entry.category) {
        this.$set(this.feeForm, "category", entry.category);
      }
      if (price !== null) {
        this.$set(this.feeForm, "unit_price_ht", price);
      }

      this.closeFeeLabels();
    },
    submitFee() {
      const payload = { ...this.feeForm };
      const id = this.$route.params.id;

      if (payload.id) {
        this.$store.dispatch("location_for_account/saveFee", {
          id: id, feeId: payload.id, fee: payload,
        });
      } else {
        this.$store.dispatch("location_for_account/addFee", { id: id, fee: payload });
      }
    },
    removeFee(fee) {
      this.feeToDelete = fee;
      this.$root.$emit("bv::show::modal", "lpc-fee-delete");
    },
    confirmRemoveFee() {
      const fee = this.feeToDelete;
      if (!fee) {
        return;
      }

      this.$store.dispatch("location_for_account/deleteFee", {
        id: this.$route.params.id,
        feeId: fee.id,
      });
      this.feeToDelete = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/_variables.scss";

// Message d'erreur de saisie, sous le champ qu'il concerne. Le rouge vient de `text-danger`,
// comme partout ailleurs dans le back-office ; `$danger` est ici une couleur de FOND, pas de
// texte, et sortirait illisible. Ces champs n'ont pas d'`invalid-feedback` bootstrap :
// l'autocomplete n'est pas un `<input>` et ne déclenche pas la validation native.
.lpc-field-error {
  margin-top: 6px;
  font-size: 12px;
}

// Suggestions de libellés de frais, déroulées sous le champ.
.lpc-suggest {
  position: relative;
}

.lpc-suggest__list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  // Au-dessus des champs qui suivent dans la fenêtre : sans quoi la liste passerait
  // DERRIÈRE le select « Catégorie ».
  z-index: 10;
  max-height: 220px;
  overflow-y: auto;
  margin: 2px 0 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid $border-10p;
  border-radius: 3px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.lpc-suggest__item {
  display: flex;
  align-items: baseline;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;

  &.is-active {
    background: $primary;
    color: #fff;
  }
}

.lpc-suggest__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Catégorie et montant par défaut : ce que la suggestion va remplir, poussé en bout de ligne.
.lpc-suggest__meta {
  margin-left: auto;
  padding-left: 12px;
  font-size: 11px;
  white-space: nowrap;
  color: $text-50p;

  .is-active & {
    color: rgba(255, 255, 255, 0.85);
  }
}

.lpc-suggest__empty,
.lpc-suggest__more {
  padding: 6px 12px;
  font-size: 11px;
  color: $text-50p;
}

.lpc-suggest__more {
  border-top: 1px solid $border-10p;
  background: #fafbfc;

  i {
    margin-right: 4px;
  }
}

// Création d'un loueur : un lien discret sous le champ, qui ouvre sa propre fenêtre — on
// chiffre, on ne tient pas le référentiel des sociétés.
.lpc-renter__toggle {
  display: inline-block;
  margin: 6px 0 0 20px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1rem;
  color: $text-50p;

  &:hover { color: $primary; }

  i { margin-right: 6px; color: $primary; }
}


.ibox-content ul.lpc-attachments {
  list-style: none;
  padding: 0;
  margin: 0;

  li + li { margin-top: 6px; }
}

.lpc-attachment-viewer {
  display: block;
  width: 100%;
  height: 75vh;
  border: 0;
}

.lpc-attachment-image {
  display: block;
  max-width: 100%;
  max-height: 75vh;
  margin: 0 auto;
}

// Un titre d'ibox qui porte une action : le libellé à gauche, le bouton en bout de ligne.
// Le titre n'a pas de retrait bas dans la charte ; le bouton, lui, en a besoin.
.ibox .ibox-title.lpc-title--action {
  display: flex;
  align-items: center;
  padding-bottom: 10px;
}

.ibox-content ul.lpc-prices {
  margin-bottom: 0;

  li {
    padding: 4px 0;
    border-bottom: 1px solid $border-10p;
    margin-bottom: 0;

    &:last-child {
      border-bottom: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/LocationForAccount/LocationForAccountDetail.vue